import * as Collapsible from '@radix-ui/react-collapsible';
import { ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Explain } from './Explain';
import { STATUS_META } from './primitives';
import { cn } from '@/lib/cn';
import type { SourceResult, SourceStatus } from '@/types';

/**
 * Every data section wears the same shell so its state is unmissable.
 *
 * Crucially, a section renders its OWN status: an unreachable source shows the
 * error rather than an empty table, so nobody reads "nothing here" as "nothing
 * happened".
 */
export function Section({
  id,
  title,
  term,
  source,
  count,
  children,
  defaultOpen = true,
  note,
}: {
  id: string;
  title: string;
  term?: string;
  source?: SourceResult<unknown>;
  count?: number;
  children: ReactNode;
  defaultOpen?: boolean;
  note?: ReactNode;
}) {
  const status = source?.status;
  const [open, setOpen] = useState(defaultOpen && status !== 'skipped');

  return (
    <Collapsible.Root open={open} onOpenChange={setOpen} asChild>
      <section id={id} className="scroll-mt-[92px] border-b border-hairline">
        <Collapsible.Trigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-2 bg-surface px-4 py-2 text-left hover:bg-surface-raised"
          >
            <ChevronDown
              size={12}
              className={cn(
                'shrink-0 text-text-faint transition-transform',
                !open && '-rotate-90',
              )}
            />
            <span className="font-condensed text-[12px] font-semibold uppercase tracking-wider text-text">
              {term ? <Explain term={term}>{title}</Explain> : title}
            </span>

            {status && <StatusPill status={status} count={count} />}
            {note && <span className="ml-1 text-[11px] text-text-faint">{note}</span>}
          </button>
        </Collapsible.Trigger>

        <Collapsible.Content>
          {status === 'unavailable' ? (
            <Unavailable error={source?.error} />
          ) : status === 'skipped' ? (
            <Skipped />
          ) : status === 'empty' ? (
            <Empty />
          ) : (
            <div className="rise">{children}</div>
          )}
        </Collapsible.Content>
      </section>
    </Collapsible.Root>
  );
}

function StatusPill({ status, count }: { status: SourceStatus; count?: number }) {
  const meta = STATUS_META[status];
  return (
    <Explain term={meta.glossary} className="no-underline">
      <span
        className={cn(
          'inline-flex items-center gap-1 border px-1.5 py-[1px]',
          'font-mono text-[10px]',
          status === 'unavailable' ? 'border-crit/40' : 'border-hairline',
        )}
      >
        <span className={meta.color}>{meta.glyph}</span>
        <span className="text-text-dim">{meta.label}</span>
        {status === 'ok' && count !== undefined && (
          <span className="tnum text-text-faint">{count}</span>
        )}
      </span>
    </Explain>
  );
}

function Unavailable({ error }: { error?: string }) {
  return (
    <div className="border-l-2 border-l-crit bg-crit-bg px-4 py-3">
      <p className="text-[12.5px] text-text">
        This source could not be reached, so its data is <strong>unknown</strong> — not
        absent. Do not conclude anything from its silence.
      </p>
      {error && <p className="mt-1 font-mono text-[11px] text-crit">{error}</p>}
    </div>
  );
}

function Empty() {
  return (
    <p className="px-4 py-3 text-[12px] text-text-dim">
      Reached successfully — this source genuinely has nothing for this bet.
    </p>
  );
}

function Skipped() {
  return (
    <p className="px-4 py-3 text-[12px] text-text-dim">
      Not queried — either not configured in this panel, or not applicable to this bet.
    </p>
  );
}
